import Button from "atoms/buttons/button";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import Icon from "atoms/icons/icon";
import { MemoryStatus } from "models/enumerations/conversations/memory-status";
import Suggestion, {
    SuggestionAction,
} from "organisms/modals/chat-modal/tool-calls/suggestion";
import React from "react";
import { MAX_MEMORIES_PER_USER } from "utilities/hooks/domain/chat/use-memories";
import { t } from "utilities/localization-utils";

export interface MemorySuggestionProps {
    content?: string;
    count: number;
    status?: MemoryStatus;
    isSaving?: boolean;
    isDismissing?: boolean;
    onSave: () => void;
    onDismiss: () => void;
    onManage: () => void;
    onUndo: () => void;
}

const BASE_CLASS = "c-suggestion";
const STATUS_CLASS = "c-suggestion-status";

const MemorySuggestion: React.FC<MemorySuggestionProps> = ({
    content,
    count,
    status,
    isSaving,
    isDismissing,
    onSave,
    onDismiss,
    onManage,
    onUndo,
}) => {
    if (status === MemoryStatus.Accepted) {
        return <Accepted count={count} onManage={onManage} />;
    }

    if (status === MemoryStatus.Rejected) {
        return <Rejected onUndo={onUndo} />;
    }

    const hasMaxMemories = count >= MAX_MEMORIES_PER_USER;

    const primaryAction: SuggestionAction = hasMaxMemories
        ? {
              label: t("actionItem", {
                  action: t("manage"),
                  item: t("memories").toLowerCase(),
              }),
              onClick: onManage,
              disabled: isDismissing,
          }
        : {
              label: t("actionItem", {
                  action: t("save"),
                  item: t("memory"),
              }),
              onClick: onSave,
              isLoading: isSaving,
              disabled: isDismissing,
          };

    const suggestionText = hasMaxMemories
        ? t("memory-suggestion-limit-reached", { max: MAX_MEMORIES_PER_USER })
        : t("memory-suggestion-prompt");

    return (
        <>
            <Suggestion
                cssClassName={hasMaxMemories ? "-max-memories" : ""}
                supportingText={suggestionText}
                primaryAction={primaryAction}
                secondaryAction={{
                    label: t("not-now"),
                    onClick: onDismiss,
                    isLoading: isDismissing,
                    disabled: isSaving,
                }}>
                {(supportingText) => (
                    <>
                        <header className={`${BASE_CLASS}__header`}>
                            <span className={`${BASE_CLASS}__tag`}>
                                <Icon
                                    type={Icons.Sparkle}
                                    size={IconSizes.Small}
                                    cssClassName={`${BASE_CLASS}__tag-icon`}
                                />
                                <span>New memory</span>
                            </span>
                            <MemorySlotsCount count={count} />
                        </header>
                        <div className={`${BASE_CLASS}__content`}>
                            <p
                                className={`${BASE_CLASS}__supporting-text`}
                                dangerouslySetInnerHTML={{
                                    __html: supportingText,
                                }}
                            />

                            {content && (
                                <p className={`${BASE_CLASS}__headline`}>
                                    {content}
                                </p>
                            )}
                        </div>
                    </>
                )}
            </Suggestion>
            <div style={{ height: "4px" }} />
        </>
    );
};

export default MemorySuggestion;

const MemorySlotsCount: React.FC<{ count: number }> = ({ count }) => {
    if (count >= MAX_MEMORIES_PER_USER) {
        return (
            <span className={`${BASE_CLASS}__slots -warn`}>
                <Icon
                    type={Icons.Warning}
                    size={IconSizes.Small}
                    cssClassName={`${BASE_CLASS}__slots-icon`}
                />
                {t("memory-slots-full", {
                    count,
                    max: MAX_MEMORIES_PER_USER,
                })}
            </span>
        );
    }

    if (count === MAX_MEMORIES_PER_USER - 1) {
        return (
            <span className={`${BASE_CLASS}__slots -warn`}>
                <Icon
                    type={Icons.Warning}
                    size={IconSizes.Small}
                    cssClassName={`${BASE_CLASS}__slots-icon`}
                />
                {t("memory-last-slot")}
            </span>
        );
    }

    return (
        <span className={`${BASE_CLASS}__count`}>
            {t("count-of-max", { count, max: MAX_MEMORIES_PER_USER })}
        </span>
    );
};

const Accepted: React.FC<{ count: number; onManage: () => void }> = ({
    count,
    onManage,
}) => {
    return (
        <>
            <div className={`${STATUS_CLASS} -accepted`}>
                <Icon
                    type={Icons.Checkmark}
                    size={IconSizes.Small}
                    cssClassName={`${STATUS_CLASS}__icon`}
                />
                <span className={`${STATUS_CLASS}__label`}>
                    {t("saved-to-memory")}
                </span>
                <span className={`${STATUS_CLASS}__separator`}>&middot;</span>
                <span>
                    {t("count-of-max", { count, max: MAX_MEMORIES_PER_USER })}
                </span>
                <span className={`${STATUS_CLASS}__separator`}>&middot;</span>
                <Button
                    onClick={onManage}
                    size={ButtonSizes.Small}
                    style={ButtonStyles.Text}>
                    {t("manage")}
                </Button>
            </div>
            <div style={{ height: "4px" }} />
        </>
    );
};

const Rejected: React.FC<{ onUndo: () => void }> = ({ onUndo }) => {
    return (
        <>
            <div className={`${STATUS_CLASS} -rejected`}>
                <Icon
                    type={Icons.Close}
                    size={IconSizes.Small}
                    cssClassName={`${STATUS_CLASS}__icon`}
                />
                <span>{t("not-saved-to-memory")}</span>
                <span className={`${STATUS_CLASS}__separator`}>&middot;</span>
                <Button
                    onClick={onUndo}
                    size={ButtonSizes.Small}
                    style={ButtonStyles.Text}>
                    {t("undo")}
                </Button>
            </div>
            <div style={{ height: "4px" }} />
        </>
    );
};
